import { Stack } from '@mui/material';

import Note from '@/designs-dev/annotations/Note';
import { type HandoverSpec } from '@/designs-dev/handover/types';
import Flow from '@/karehero-app/tell-us-once/intake/Flow';

// Onboarding 1.1, as a flow. Every screen is Onboarding's, opened at the same
// frame with `version='1.1'`, so the two pages read side by side and this one
// shows only what changed.
const at = (initial: Parameters<typeof Flow>[0]['initial']) => () => (
  <Flow initial={initial} option={2} version='1.1' />
);

// The doc's option, named the way the doc names it, so a note here can be
// found in the doc.
const idea = (title: string, body: string) => (
  <Note title={title} colour='sunflower' variant='body2'>
    {body}
  </Note>
);

export const onboarding11Spec: HandoverSpec = {
  id: 'onboarding-1-1',
  title: 'Onboarding 3.1',
  description: '',
  links: [
    {
      label: 'Onboarding 1.0',
      url: '/tell-us-once/flows/intake-profile-review',
    },
    {
      label: 'Walk it in the prototype',
      url: '/tell-us-once/intake-profile-review?scene=v11-youre-in',
    },
  ],
  flows: [
    {
      title: 'Warmed up',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              Onboarding with the recommended set from "Warming up onboarding"
              (6 October), the answer to Steph's feedback: the flow asks without
              giving anything back, the words are correct but cold, and nothing
              in it feels like KareHero.
            </span>
            <span>
              Same screens, same questions, same order. Everything is copy, or a
              card attached to a step that already exists, because saved
              progress is by step index.
            </span>
            <span>The yellow notes name the doc's option on each screen.</span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'youre-in',
          title: "You're in",
          tap: "Tap 'Continue'",
          render: at('youre-in'),
          note: idea(
            'Copy table',
            "Says what they get. 'Shape the right support' was a brochure line.",
          ),
        },
        {
          id: 'ai-consent',
          title: 'Before we start',
          tap: "'That's fine with me'",
          render: at('ai-consent'),
          note: idea(
            '2A · Plain and honest',
            "Same screen, same two buttons. Says what they can do, what AI is for, and that a person is always involved. 'Process data' was the coldest phrase in the flow, on the first screen that asks for trust. Waits on legal's wording review.",
          ),
        },
        {
          id: 'fork',
          title: 'Are you supporting someone?',
          tap: "'Yes, I'm helping look after someone'",
          render: at('chat-v2-fork'),
          note: idea(
            '4A · Name, not face',
            'Keni named once, in the first bubble. The orb stays the only visual.',
          ),
        },
        {
          id: 'carer-name',
          title: 'Worth knowing',
          tap: "'Yes, that's me'",
          render: at('chat-v2-carer-name'),
          note: idea(
            '1B · A number that matters',
            "The first thing given back, straight after the fork: a card from us, not a bubble. Keyed to the fork. This is the carer's version; the self version would be the same by age, or PIP, and exploring gets nothing. The rates change every April. The name question underneath says why it matters, without the word 'legal'.",
          ),
        },
        {
          id: 'preferred-name',
          title: 'What should we call you?',
          tap: "'No, Vez is fine'",
          render: at('chat-v2-preferred-name'),
          note: idea('Copy table', 'Shorter and warmer.'),
        },
        {
          id: 'who',
          title: 'What this is for, and who reads it',
          tap: 'Type their name',
          render: at('chat-v2-who'),
          note: idea(
            '1A and 1C',
            "What the answers go into and what it saves them, then the care experts by name and the promise of a call. Initials stand in for the team photo. The call promise has to be true for every tenant before it ships; if not, it says 'will read this' and stops there. 'ID' is gone from the question.",
          ),
        },
        {
          id: 'situation',
          title: 'The open ask, warmer',
          tap: 'Talk or type',
          render: at('chat-v2-situation'),
          note: idea(
            '2D',
            'Uses her name, gives permission to stop and start, and points at the plus for a letter. Same bubbles, same orb.',
          ),
        },
        {
          id: 'anything-more',
          title: 'Anything else?',
          tap: "'That's everything'",
          render: at('chat-v2-anything-more'),
          note: idea('Copy table', "'Is that it' could read as impatience."),
        },
        {
          id: 'processing',
          title: 'The wait shows the work',
          tap: 'It finishes reading',
          render: at('processing'),
          note: idea(
            '3A, resolving into 3C',
            "Steps keyed to what they sent, ticking as each one lands, then what we're putting together. In the app each step is an extraction event; here they're on a timer. 'Checking what Margaret could claim' is timed until eligibility exists, so it says checking, never found.",
          ),
        },
        {
          id: 'summary',
          title: 'Does this look right?',
          tap: "'Yes, that's correct'",
          render: at('summary'),
        },
        {
          id: 'review-facts',
          title: 'About Margaret',
          tap: "'Looks good to me'",
          render: at('review-facts'),
          note: idea('Copy table', 'Kept. Already in the right voice.'),
        },
      ],
      noteAfter: (
        <Note title='Not in 1.1' colour='cherry' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              4B, a still Keni at three pauses. Waits on the brand audit's final
              drawing; the sketch in the doc is a stand-in and shouldn't ship.
            </span>
            <span>
              2B and 2C, the other consent wordings. 2C waits on legal deciding
              consent can read as a promise.
            </span>
            <span>
              3B, a quiet Keni on the processing screen. Only if Keni is
              adopted.
            </span>
            <span>
              The doc's 'Snap a letter or their ID instead' row on their name.
              The prototype no longer has that row.
            </span>
          </Stack>
        </Note>
      ),
    },
    {
      title: 'When we cannot read it',
      steps: [
        {
          id: 'summary-problem',
          title: 'What we did get',
          tap: 'Opened from the ping',
          render: at('summary-problem'),
          note: idea(
            'Copy table',
            "Never 'wrong with your answers'. Offers a care expert.",
          ),
        },
      ],
    },
  ],
  states: [],
  components: {},
};
